import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Search, Plus, Minus, X, Syringe, Stethoscope, Scissors, Calendar, ChevronDown, ClipboardList, Package, Check, ToggleLeft, ToggleRight, Banknote, CreditCard, PawPrint, FileText } from 'lucide-react';
import PetAvatar from '../components/PetAvatar';
import PetIcon from '../components/PetIcon';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { getCustomers, getClinicOperations, getProducts, createOperationsBatch, createPet, getParasutSettings, createBatchInvoice } from '../services/api';
import Loader from '../components/Loader';
import { toast } from 'sonner';

const INTERVAL_OPTIONS = [
  { label: '1 Hafta', days: 7 },
  { label: '2 Hafta', days: 14 },
  { label: '1 Ay', days: 30 },
  { label: '2 Ay', days: 60 },
  { label: '3 Ay', days: 90 },
  { label: '6 Ay', days: 180 },
  { label: '1 Yıl', days: 365 },
  { label: '2 Yıl', days: 730 },
];

const TREATMENT_INTERVAL_OPTIONS = [
  { label: '1 Gün', days: 1 },
  { label: '3 Gün', days: 3 },
  { label: '1 Hafta', days: 7 },
  { label: '15 Gün', days: 15 },
  { label: '1 Ay', days: 30 },
  { label: '2 Ay', days: 60 },
];

const TYPE_CFG = {
  vaccine: { icon: Syringe, label: 'Aşı', color: 'bg-[#34C759]' },
  treatment: { icon: Stethoscope, label: 'Tedavi', color: 'bg-[#6b6de3]' },
  service: { icon: Scissors, label: 'Hizmet', color: 'bg-[#FF9500]' },
  product: { icon: Package, label: 'Ürün', color: 'bg-[#AF52DE]' },
};

const AddOperation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [clinicOps, setClinicOps] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loadingOps, setLoadingOps] = useState(true);
  const [activeType, setActiveType] = useState('vaccine');
  const [productSearch, setProductSearch] = useState('');

  const [cart, setCart] = useState([]);
  const [expandedCartItem, setExpandedCartItem] = useState(null);

  const [productSelectModal, setProductSelectModal] = useState(false);
  const [pendingOp, setPendingOp] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [cashAmount, setCashAmount] = useState('');
  const [cardAmount, setCardAmount] = useState('');
  const [isCredit, setIsCredit] = useState(false);
  const [creditCollected, setCreditCollected] = useState('');
  const [creditMethod, setCreditMethod] = useState('cash');
  const [creditCash, setCreditCash] = useState('');
  const [creditCard, setCreditCard] = useState('');
  const [createInvoice, setCreateInvoice] = useState(false);
  const [tcKimlikNo, setTcKimlikNo] = useState('');
  const [parasutActive, setParasutActive] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(
    location.state?.customerName ? { id: location.state.customerId, name: location.state.customerName, pets: [] } : null
  );
  const [selectedPet, setSelectedPet] = useState(
    location.state?.petId ? { id: location.state.petId, name: location.state.petName, species: location.state.petSpecies } : null
  );
  const [showQuickPet, setShowQuickPet] = useState(false);
  const [quickPetName, setQuickPetName] = useState('');
  const [quickPetSpecies, setQuickPetSpecies] = useState('dog');
  const [addingPet, setAddingPet] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [opsR, prodsR] = await Promise.all([getClinicOperations(), getProducts()]);
      setClinicOps(opsR.data);
      setAllProducts(prodsR.data);
      try {
        const pRes = await getParasutSettings();
        const ps = pRes.data;
        setParasutActive(ps?.is_active && ps?.client_id && ps?.firma_no ? true : false);
      } catch {}
    } catch (e) { console.error(e); }
    finally { setLoadingOps(false); }
  };

  const handleSearch = useCallback(async (query) => {
    if (!query || query.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try { const r = await getCustomers(query); setSearchResults(r.data); }
    catch (e) { console.error(e); }
    finally { setSearching(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => handleSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery, handleSearch]);

  const handleCustomerSelect = (customer) => { setSelectedCustomer(customer); setSearchQuery(''); setSearchResults([]); };
  const handlePetSelect = (pet) => { setSelectedPet(pet); };

  const handleQuickAddPet = async () => {
    if (!quickPetName.trim()) { toast.error('Hasta adı gerekli'); return; }
    setAddingPet(true);
    try {
      const res = await createPet({ customer_id: selectedCustomer.id, name: quickPetName.trim(), species: quickPetSpecies });
      const newPet = { id: res.data.id, name: quickPetName.trim(), species: quickPetSpecies };
      setSelectedCustomer(prev => ({ ...prev, pets: [...(prev.pets || []), newPet] }));
      setSelectedPet(newPet);
      setShowQuickPet(false);
      setQuickPetName('');
      setQuickPetSpecies('dog');
      toast.success(`${newPet.name} eklendi`);
    } catch (e) { toast.error('Hasta eklenemedi'); }
    finally { setAddingPet(false); }
  };

  // === CART LOGIC ===
  const addOpToCart = (op) => {
    // Vaccine: only 1 allowed. Others: allow duplicates (increase qty)
    if (op.type === 'vaccine') {
      const exists = cart.find(c => c.name === op.name && c.type === op.type);
      if (exists) { toast.info(`${op.name} zaten eklendi`); return; }
    }

    const linked = op.linked_products || [];
    if (linked.length > 1) {
      setPendingOp(op);
      setProductSelectModal(true);
      return;
    }

    // For non-vaccine, check if already in cart and bump qty
    if (op.type !== 'vaccine') {
      const existing = cart.find(c => c.name === op.name && c.type === op.type);
      if (existing) {
        setCart(prev => prev.map(c => c.id === existing.id ? { ...c, quantity: c.quantity + 1 } : c));
        toast.success(`${op.name} x${existing.quantity + 1}`);
        return;
      }
    }

    // Determine default price: if linked product has price, use it; else use op's default_price
    let defaultPrice = op.default_price || 0;
    if (linked.length === 1) {
      const linkedProduct = allProducts.find(p => p.id === linked[0].product_id);
      if (linkedProduct?.default_price) defaultPrice = linkedProduct.default_price;
    }

    finalizeAddOpToCart(op, linked.length === 1 ? linked[0].product_id : null, defaultPrice);
  };

  const finalizeAddOpToCart = (op, deductProductId, defaultPrice) => {
    let nextDueDate = null;
    let intervalOption = null;
    if (op.type === 'vaccine' && op.default_interval_days) {
      intervalOption = INTERVAL_OPTIONS.find(o => o.days === op.default_interval_days) || { label: `${op.default_interval_days} gün`, days: op.default_interval_days };
      const d = new Date(); d.setDate(d.getDate() + op.default_interval_days);
      nextDueDate = d.toISOString().split('T')[0];
    }
    const linked = op.linked_products || [];
    const selectedProduct = deductProductId ? linked.find(lp => lp.product_id === deductProductId) : null;

    setCart(prev => [...prev, {
      id: Date.now() + Math.random(),
      type: op.type,
      name: op.name,
      price: defaultPrice > 0 ? String(defaultPrice) : '',
      note: '',
      quantity: 1,
      next_due_date: nextDueDate,
      intervalOption,
      isRepeating: false,
      treatmentInterval: null,
      repeatCount: 1,
      deduct_product_id: deductProductId,
      deduct_product_name: selectedProduct?.product_name || null,
      vat_rate: op.vat_rate,
    }]);
    toast.success(`${op.name} eklendi`);
  };

  const handleProductSelect = (productId) => {
    if (!pendingOp) return;
    const linked = pendingOp.linked_products || [];
    const linkedProduct = allProducts.find(p => p.id === productId);
    const defaultPrice = linkedProduct?.default_price || pendingOp.default_price || 0;
    finalizeAddOpToCart(pendingOp, productId, defaultPrice);
    setPendingOp(null);
    setProductSelectModal(false);
  };

  // Add product directly
  const addProductToCart = (product) => {
    const existing = cart.find(c => c.type === 'product' && c.deduct_product_id === product.id);
    if (existing) {
      setCart(prev => prev.map(c => c.id === existing.id ? { ...c, quantity: c.quantity + 1 } : c));
      toast.success(`${product.name} x${existing.quantity + 1}`);
      return;
    }
    setCart(prev => [...prev, {
      id: Date.now() + Math.random(),
      type: 'product',
      name: product.name,
      price: product.default_price > 0 ? String(product.default_price) : '',
      note: '',
      quantity: 1,
      next_due_date: null,
      intervalOption: null,
      isRepeating: false,
      treatmentInterval: null,
      repeatCount: 1,
      deduct_product_id: product.id,
      deduct_product_name: product.name,
      vat_rate: product.vat_rate,
    }]);
    toast.success(`${product.name} eklendi`);
  };

  const removeFromCart = (cartId) => {
    setCart(prev => prev.filter(c => c.id !== cartId));
    if (expandedCartItem === cartId) setExpandedCartItem(null);
  };

  const updateCartItem = (cartId, field, value) => {
    setCart(prev => prev.map(c => c.id === cartId ? { ...c, [field]: value } : c));
  };

  const changeQuantity = (cartId, delta) => {
    setCart(prev => prev.map(c => {
      if (c.id !== cartId) return c;
      const newQty = Math.max(1, c.quantity + delta);
      return { ...c, quantity: newQty };
    }));
  };

  const totalPrice = cart.reduce((sum, c) => sum + (parseFloat(c.price) || 0) * c.quantity, 0);

  const openPaymentModal = () => {
    if (!selectedPet) { toast.error('Lütfen bir hasta seçin'); return; }
    if (cart.length === 0) { toast.error('En az bir işlem ekleyin'); return; }
    setPaymentMethod('cash');
    setCashAmount(totalPrice.toFixed(0));
    setCardAmount('');
    setIsCredit(false);
    setCreditCollected('');
    setCreditMethod('cash');
    setCreditCash('');
    setCreditCard('');
    setShowPaymentModal(true);
  };

  const handleSubmit = async () => {
    if (createInvoice && tcKimlikNo.length !== 11) {
      toast.error('Fatura kesmek için geçerli bir TC Kimlik No giriniz (11 hane)');
      return;
    }
    setLoading(true);
    try {
      const operations = cart.map(c => {
        let note = c.note || '';
        if (c.type === 'treatment' && c.isRepeating && c.treatmentInterval) {
          const info = `[Tekrarlanan: ${c.repeatCount}x, Her ${c.treatmentInterval.label}]`;
          note = note ? `${info} ${note}` : info;
        }
        let nextDue = c.next_due_date;
        if (c.type === 'treatment' && c.isRepeating && c.treatmentInterval) {
          nextDue = new Date(Date.now() + c.treatmentInterval.days * 86400000).toISOString().split('T')[0];
        }
        return {
          pet_id: selectedPet.id, type: c.type, name: c.name,
          price: parseFloat(c.price) || 0, note: note || null,
          next_due_date: nextDue, deduct_product_id: c.deduct_product_id || null,
          quantity: c.quantity,
        };
      });

      let finalCash = 0, finalCard = 0;
      if (paymentMethod === 'cash') { finalCash = totalPrice; finalCard = 0; }
      else if (paymentMethod === 'card') { finalCash = 0; finalCard = totalPrice; }
      else { finalCash = parseFloat(cashAmount) || 0; finalCard = parseFloat(cardAmount) || 0; }

      await createOperationsBatch({
        pet_id: selectedPet.id,
        operations,
        payment_method: paymentMethod,
        cash_amount: finalCash,
        card_amount: finalCard,
        is_credit: isCredit,
        credit_collected: isCredit ? (parseFloat(creditCollected) || 0) : 0,
        credit_collected_method: isCredit ? creditMethod : 'cash',
      });

      if (createInvoice && parasutActive && selectedCustomer) {
        try {
          const invoiceItems = cart.map(c => ({
            name: c.name,
            price: parseFloat(c.price) || 0,
            quantity: c.quantity || 1,
            vat_rate: c.vat_rate != null ? parseFloat(c.vat_rate) : 20,
          }));
          await createBatchInvoice({
            customer_id: selectedCustomer.id,
            tc_kimlik_no: tcKimlikNo,
            items: invoiceItems,
          });
          toast.success('Fatura oluşturuldu');
        } catch (e) {
          toast.error('İşlemler kaydedildi ama fatura oluşturulamadı: ' + (e.response?.data?.detail || ''));
        }
      }

      toast.success(`${cart.length} işlem kaydedildi`);
      setShowPaymentModal(false);
      navigate(-1);
    } catch (e) {
      const errDetail = e.response?.data?.detail;
      const msg = typeof errDetail === 'string' ? errDetail : Array.isArray(errDetail) ? errDetail.map(d => d.msg).join(', ') : 'Bir hata oluştu';
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const filteredOps = clinicOps.filter(op => {
    if (activeType !== 'product' && op.type !== activeType) return false;
    if (selectedPet && op.animal_type !== 'all' && op.animal_type !== selectedPet.species) return false;
    return true;
  });

  const filteredProducts = allProducts.filter(p => {
    if (!productSearch) return true;
    const q = productSearch.toLowerCase();
    return p.name.toLowerCase().includes(q) || (p.barcode && p.barcode.toLowerCase().includes(q));
  });

  const handleProductSearchKeyDown = (e) => {
    if (e.key === 'Enter' && productSearch) {
      e.preventDefault();
      const exactMatch = allProducts.find(p => p.barcode && p.barcode.toLowerCase() === productSearch.toLowerCase());
      if (exactMatch) {
        addProductToCart(exactMatch);
        setProductSearch('');
      }
    }
  };

  const isInCart = (opName, opType) => cart.some(c => c.name === opName && c.type === opType);

  return (
    <div className="min-h-screen bg-[#F2F2F7]">
      <header className="bg-white px-4 pt-12 pb-4 shadow-sm sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-gray-100" data-testid="back-btn">
            <ArrowLeft size={24} className="text-gray-900" />
          </button>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900">{`İşlem Ekle`}</h1>
          {cart.length > 0 && (
            <div className="ml-auto flex items-center gap-1.5 bg-[#6b6de3] text-white px-3 py-1.5 rounded-full text-sm font-medium" data-testid="cart-badge">
              <ClipboardList size={14} />
              {cart.length}
            </div>
          )}
        </div>
      </header>

      <div className="p-4 space-y-4 pb-48">
        {/* Customer Search */}
        {!selectedCustomer ? (
          <div className="ios-card">
            <Label className="text-sm font-semibold text-[#8E8E93] uppercase tracking-wider">Müşteri Seç</Label>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={20} />
              <Input type="search" placeholder="Telefon veya isim ile ara..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 text-base bg-[#F2F2F7] border-0 rounded-xl focus:ring-2 focus:ring-[#6b6de3]" autoFocus data-testid="customer-search-input" />
            </div>
            {searchQuery.length >= 2 && (
              <div className="mt-3 rounded-xl border border-gray-100 overflow-hidden">
                {searching ? <div className="p-4 flex justify-center"><Loader size="sm" /></div> :
                  searchResults.length > 0 ? (
                    <div className="divide-y divide-gray-100">
                      {searchResults.map((c) => (
                        <button key={c.id} onClick={() => handleCustomerSelect(c)}
                          className="w-full p-3 flex items-center justify-between hover:bg-gray-50 transition-colors" data-testid={`customer-select-${c.id}`}>
                          <div className="text-left">
                            <p className="font-medium text-gray-900">{c.name}</p>
                            <p className="text-sm text-[#8E8E93]">{c.phone}</p>
                          </div>
                          <span className="text-xs text-[#6b6de3]">{c.pets?.length || 0} hasta</span>
                        </button>
                      ))}
                    </div>
                  ) : <div className="p-4 text-center text-[#8E8E93]">Müşteri bulunamadı</div>}
              </div>
            )}
          </div>
        ) : !selectedPet ? (
          <div className="ios-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm text-[#8E8E93]">Müşteri</p>
                <p className="font-semibold text-gray-900">{selectedCustomer.name}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setSelectedCustomer(null); setSelectedPet(null); setCart([]); }} className="text-[#6b6de3]">Değiştir</Button>
            </div>
            <Label className="text-sm font-semibold text-[#8E8E93] uppercase tracking-wider">Hasta Seç</Label>
            <div className="mt-2 space-y-2">
              {selectedCustomer.pets?.map((pet) => (
                <button key={pet.id} onClick={() => handlePetSelect(pet)}
                  className="w-full p-4 bg-[#F2F2F7] rounded-xl flex items-center gap-3 hover:bg-gray-200 transition-colors" data-testid={`pet-select-${pet.id}`}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center">
                    <PetAvatar species={pet.species} gender={pet.gender} size={22} />
                  </div>
                  <span className="font-medium text-gray-900">{pet.name}</span>
                </button>
              ))}

              {/* Quick Add Pet */}
              {!showQuickPet ? (
                <button onClick={() => setShowQuickPet(true)}
                  className="w-full p-4 border-2 border-dashed border-[#C7C7CC] rounded-xl flex items-center justify-center gap-2 text-[#6b6de3] font-medium hover:bg-blue-50 transition-colors"
                  data-testid="quick-add-pet-btn">
                  <Plus size={18} />
                  Yeni Hasta Ekle
                </button>
              ) : (
                <div className="p-4 bg-blue-50 rounded-xl space-y-3" data-testid="quick-add-pet-form">
                  <Input
                    type="text"
                    placeholder="Hasta adı"
                    value={quickPetName}
                    onChange={(e) => setQuickPetName(e.target.value)}
                    className="h-11 text-sm bg-white border-0 rounded-xl"
                    autoFocus
                    data-testid="quick-pet-name-input"
                  />
                  <div className="flex gap-2">
                    {[
                      { value: 'dog', label: 'Köpek' },
                      { value: 'cat', label: 'Kedi' },
                      { value: 'bird', label: 'Kuş' },
                      { value: 'other', label: 'Diğer' },
                    ].map((s) => (
                      <button key={s.value} onClick={() => setQuickPetSpecies(s.value)}
                        className={`flex-1 py-2 rounded-xl text-xs font-medium flex items-center justify-center gap-1 transition-colors ${
                          quickPetSpecies === s.value ? 'bg-[#6b6de3] text-white' : 'bg-white text-gray-700'
                        }`}
                        data-testid={`species-${s.value}`}>
                        <PetIcon species={s.value} size={14} />
                        {s.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" className="flex-1 h-10 text-sm rounded-xl" onClick={() => { setShowQuickPet(false); setQuickPetName(''); }} data-testid="quick-pet-cancel">
                      Vazgeç
                    </Button>
                    <Button className="flex-1 h-10 text-sm bg-[#6b6de3] hover:bg-[#5658c4] rounded-xl" onClick={handleQuickAddPet} disabled={addingPet} data-testid="quick-pet-save">
                      {addingPet ? 'Ekleniyor...' : 'Ekle'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Selected Customer & Pet */}
            <div className="ios-card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center">
                    <PetAvatar species={selectedPet.species} gender={selectedPet.gender} size={22} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{selectedPet.name}</p>
                    <p className="text-sm text-[#8E8E93]">{selectedCustomer.name}</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setSelectedCustomer(null); setSelectedPet(null); setCart([]); }} className="text-[#6b6de3]">Değiştir</Button>
              </div>
            </div>

            {/* Type Tabs */}
            <div className="flex gap-1.5">
              {(['vaccine', 'treatment', 'service', 'product']).map((type) => {
                const cfg = TYPE_CFG[type];
                const Icon = cfg.icon;
                return (
                  <button key={type} onClick={() => setActiveType(type)}
                    className={`flex-1 py-2.5 rounded-xl font-medium text-xs flex items-center justify-center gap-1 transition-colors ${
                      activeType === type ? `${cfg.color} text-white` : 'bg-white text-gray-900'
                    }`} data-testid={`type-${type}`}>
                    <Icon size={14} />
                    {cfg.label}
                  </button>
                );
              })}
            </div>

            {/* Operation/Product Grid */}
            {activeType !== 'product' ? (
              <div className="ios-card">
                <Label className="text-sm font-semibold text-[#8E8E93] uppercase tracking-wider mb-3 block">
                  {TYPE_CFG[activeType].label} Seç
                </Label>
                {loadingOps ? <div className="py-4 flex justify-center"><Loader size="sm" /></div> : (
                  <div className="grid grid-cols-2 gap-2">
                    {filteredOps.map((op) => {
                      const inCart = isInCart(op.name, op.type);
                      return (
                        <button key={op.id} onClick={() => addOpToCart(op)}
                          className={`p-3 rounded-xl text-sm font-medium text-left transition-all ${
                            inCart && op.type === 'vaccine' ? 'bg-[#6b6de3] text-white ring-2 ring-[#6b6de3]/30' : 'bg-[#F2F2F7] text-gray-900 hover:bg-gray-200'
                          }`} data-testid={`op-${op.name}`}>
                          <div className="flex items-center justify-between">
                            <span className="truncate">{op.name}</span>
                            {inCart && op.type === 'vaccine'
                              ? <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded-full shrink-0">Eklendi</span>
                              : <Plus size={16} className="text-[#8E8E93] shrink-0" />}
                          </div>
                          {op.default_price > 0 && (
                            <p className={`text-xs mt-0.5 ${inCart && op.type === 'vaccine' ? 'text-white/70' : 'text-green-600'}`}>{op.default_price} TL</p>
                          )}
                          {op.default_interval_days && (
                            <p className={`text-[10px] mt-0.5 ${inCart && op.type === 'vaccine' ? 'text-white/70' : 'text-[#8E8E93]'}`}>{op.default_interval_days} gün</p>
                          )}
                        </button>
                      );
                    })}
                    {filteredOps.length === 0 && (
                      <div className="col-span-2 py-4 text-center text-[#8E8E93] text-sm">
                        Bu kategoride tanımlı işlem yok
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              /* Product Tab */
              <div className="ios-card">
                <Label className="text-sm font-semibold text-[#8E8E93] uppercase tracking-wider mb-3 block">Ürün Seç</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={16} />
                  <Input type="search" placeholder="Barkod veya ürün adı ile ara..." value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    onKeyDown={handleProductSearchKeyDown}
                    className="pl-9 h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl" data-testid="product-search-input" />
                </div>
                {!productSearch ? (
                  <div className="py-6 text-center text-[#8E8E93] text-sm">
                    Barkod veya ürün adı yazarak arayın
                  </div>
                ) : filteredProducts.length > 0 ? (
                  <div className="space-y-1.5 max-h-64 overflow-y-auto mt-3">
                    {filteredProducts.map((p) => (
                      <button key={p.id} onClick={() => addProductToCart(p)}
                        className="w-full p-3 bg-[#F2F2F7] rounded-xl flex items-center justify-between hover:bg-gray-200 transition-colors"
                        data-testid={`product-add-${p.id}`}>
                        <div className="text-left min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {p.barcode && <span className="text-[10px] text-[#8E8E93]">{p.barcode}</span>}
                            <span className="text-[10px] text-[#8E8E93]">Stok: {p.current_stock}</span>
                            {p.category_name && <span className="text-[10px] text-[#8E8E93]">{p.category_name}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {p.default_price > 0 && <span className="text-xs text-green-600 font-medium">{p.default_price} TL</span>}
                          <Plus size={16} className="text-[#6b6de3]" />
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="py-4 text-center text-[#8E8E93] text-sm mt-3">Ürün bulunamadı</div>
                )}
              </div>
            )}

            {/* Cart: Sepet */}
            {cart.length > 0 && (
              <div className="ios-card" data-testid="cart-section">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-[#8E8E93] flex items-center gap-1.5">
                    <ClipboardList size={14} />
                    Sepet ({cart.length})
                  </span>
                  {totalPrice > 0 && (
                    <span className="text-sm font-bold text-gray-900" data-testid="cart-total">{totalPrice.toFixed(0)} TL</span>
                  )}
                </div>

                <div className="space-y-1">
                  {cart.map((item) => {
                    const cfg = TYPE_CFG[item.type] || TYPE_CFG.service;
                    const Icon = cfg.icon;
                    const canChangeQty = item.type !== 'vaccine';
                    return (
                      <div key={item.id} className="bg-[#F2F2F7] rounded-xl overflow-hidden" data-testid={`cart-item-${item.id}`}>
                        {/* Single compact row */}
                        <div className="flex items-center gap-1.5 px-2.5 py-2">
                          <div className={`w-6 h-6 ${cfg.color} rounded-md flex items-center justify-center text-white shrink-0`}>
                            <Icon size={12} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 text-xs truncate">{item.name}</p>
                            {item.deduct_product_name && (
                              <p className="text-[9px] text-[#6b6de3] truncate">{item.deduct_product_name}</p>
                            )}
                          </div>
                          {canChangeQty && (
                            <div className="flex items-center shrink-0 bg-white rounded-md" data-testid={`qty-control-${item.id}`}>
                              <button onClick={() => changeQuantity(item.id, -1)} className="w-6 h-6 flex items-center justify-center text-[#8E8E93]"><Minus size={12} /></button>
                              <span className="w-5 text-center text-xs font-semibold text-gray-900">{item.quantity}</span>
                              <button onClick={() => changeQuantity(item.id, 1)} className="w-6 h-6 flex items-center justify-center text-[#6b6de3]"><Plus size={12} /></button>
                            </div>
                          )}
                          <div className="relative w-20 shrink-0">
                            <Input type="number" placeholder="0" value={item.price}
                              onChange={(e) => updateCartItem(item.id, 'price', e.target.value)}
                              className="h-7 text-xs bg-white border-0 rounded-md pr-5 text-right" data-testid={`cart-price-${item.id}`} />
                            <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-[#8E8E93]">TL</span>
                          </div>
                          <button onClick={() => removeFromCart(item.id)} className="p-1 rounded-full text-[#C7C7CC] hover:text-red-500 shrink-0" data-testid={`cart-remove-${item.id}`}>
                            <X size={14} />
                          </button>
                        </div>

                        {/* Qty total */}
                        {canChangeQty && item.quantity > 1 && item.price && (
                          <div className="px-2.5 pb-1.5 -mt-0.5">
                            <span className="text-[9px] text-[#8E8E93]">{item.quantity} x {item.price} = {(item.quantity * parseFloat(item.price || 0)).toFixed(0)} TL</span>
                          </div>
                        )}

                        {/* Expand for vaccine/treatment details */}
                        {(item.type === 'vaccine' || item.type === 'treatment') && (
                          <div className="px-2.5 pb-1">
                            <button onClick={() => setExpandedCartItem(expandedCartItem === item.id ? null : item.id)}
                              className="text-[10px] text-[#6b6de3] pb-1 flex items-center gap-0.5" data-testid={`cart-expand-${item.id}`}>
                              <ChevronDown size={10} className={`transition-transform ${expandedCartItem === item.id ? 'rotate-180' : ''}`} />
                              {expandedCartItem === item.id ? 'Gizle' : 'Detay'}
                            </button>
                          </div>
                        )}

                        {expandedCartItem === item.id && (
                          <div className="px-3 pb-3 space-y-3 border-t border-gray-200 pt-3">
                            {item.type === 'vaccine' && (
                              <div className="space-y-2">
                                <Label className="text-xs font-medium text-gray-700 flex items-center gap-1">
                                  <Calendar size={12} className="text-[#34C759]" /> Sonraki Aşı
                                </Label>
                                <div className="grid grid-cols-4 gap-1">
                                  {INTERVAL_OPTIONS.map((opt) => (
                                    <button key={opt.days} type="button"
                                      onClick={() => {
                                        const d = new Date(); d.setDate(d.getDate() + opt.days);
                                        updateCartItem(item.id, 'next_due_date', d.toISOString().split('T')[0]);
                                        updateCartItem(item.id, 'intervalOption', opt);
                                      }}
                                      className={`p-1.5 rounded-lg text-[10px] font-medium transition-colors ${
                                        item.intervalOption?.days === opt.days ? 'bg-[#34C759] text-white' : 'bg-white text-gray-700'
                                      }`}>
                                      {opt.label}
                                    </button>
                                  ))}
                                </div>
                                {item.next_due_date && (
                                  <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg">
                                    <span className="text-xs text-gray-600">Sonraki:</span>
                                    <span className="text-xs font-semibold text-[#34C759]">
                                      {new Date(item.next_due_date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                    </span>
                                  </div>
                                )}
                                <Input type="date" value={item.next_due_date || ''}
                                  onChange={(e) => { updateCartItem(item.id, 'next_due_date', e.target.value); updateCartItem(item.id, 'intervalOption', null); }}
                                  className="h-9 text-xs bg-white border-0 rounded-lg" />
                              </div>
                            )}

                            {item.type === 'treatment' && (
                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <Label className="text-xs font-medium text-gray-700">Tekrarlanan tedavi?</Label>
                                  <button type="button" onClick={() => updateCartItem(item.id, 'isRepeating', !item.isRepeating)}
                                    className={`w-10 h-6 rounded-full transition-colors relative ${item.isRepeating ? 'bg-[#6b6de3]' : 'bg-gray-300'}`}>
                                    <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${item.isRepeating ? 'translate-x-4' : 'translate-x-0.5'}`} />
                                  </button>
                                </div>
                                {item.isRepeating && (
                                  <div className="space-y-2">
                                    <div className="grid grid-cols-3 gap-1">
                                      {TREATMENT_INTERVAL_OPTIONS.map((opt) => (
                                        <button key={opt.days} type="button"
                                          onClick={() => updateCartItem(item.id, 'treatmentInterval', opt)}
                                          className={`p-1.5 rounded-lg text-xs font-medium transition-colors ${
                                            item.treatmentInterval?.days === opt.days ? 'bg-[#6b6de3] text-white' : 'bg-white text-gray-700'
                                          }`}>{opt.label}</button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            <Textarea placeholder="Not (opsiyonel)..." value={item.note}
                              onChange={(e) => updateCartItem(item.id, 'note', e.target.value)}
                              className="text-xs bg-white border-0 rounded-lg min-h-[50px]" data-testid={`cart-note-${item.id}`} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Fixed Bottom Submit */}
      {selectedPet && cart.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-20 safe-area-bottom" data-testid="cart-footer">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-[#8E8E93]">{cart.length} kalem</span>
            <span className="text-lg font-bold text-gray-900">{totalPrice.toFixed(0)} TL</span>
          </div>
          <Button onClick={openPaymentModal} disabled={loading}
            className="w-full h-14 text-base font-semibold bg-[#6b6de3] hover:bg-[#5658c4] rounded-full disabled:opacity-50" data-testid="submit-operation-btn">
            {`Devam Et (${cart.length} kalem)`}
          </Button>
        </div>
      )}

      {/* Product Selection Modal */}
      <Dialog open={productSelectModal} onOpenChange={open => { if (!open) { setProductSelectModal(false); setPendingOp(null); } }}>
        <DialogContent className="sm:rounded-2xl sm:max-w-md lg:max-w-3xl sm:max-h-[75vh] overflow-hidden flex flex-col p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-base">{pendingOp?.name}</DialogTitle>
            <p className="text-sm text-[#8E8E93] mt-1">Hangi ürünün stoğu düşsün?</p>
          </DialogHeader>
          <div className="space-y-2 mt-3">
            {pendingOp?.linked_products?.map(lp => (
              <button key={lp.product_id} onClick={() => handleProductSelect(lp.product_id)}
                className="w-full flex items-center justify-between p-3 bg-[#F2F2F7] rounded-xl hover:bg-gray-200 transition-colors"
                data-testid={`select-product-${lp.product_id}`}>
                <div className="flex items-center gap-2">
                  <Package size={16} className="text-[#6b6de3]" />
                  <span className="text-sm font-medium text-gray-900">{lp.product_name}</span>
                </div>
                <Check size={16} className="text-[#8E8E93]" />
              </button>
            ))}
            <button onClick={() => { finalizeAddOpToCart(pendingOp, null, pendingOp?.default_price || 0); setPendingOp(null); setProductSelectModal(false); }}
              className="w-full p-3 text-sm text-[#8E8E93] text-center rounded-xl hover:bg-gray-100 transition-colors">
              Stok düşürme
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-x-0 top-0 bottom-[65px] z-[9998] flex sm:inset-0 sm:items-center sm:justify-center sm:p-3" data-testid="payment-modal">
          <div className="absolute inset-0 bg-black/40" onClick={() => setPaymentOpen(false)} />
          <div className="relative bg-white sm:rounded-2xl w-full sm:max-w-md lg:max-w-3xl flex flex-col h-full sm:h-auto sm:max-h-[75vh] overflow-y-auto shadow-2xl">
            <div className="p-6 space-y-5">
              {/* Header */}
              <div className="text-center">
                <h2 className="text-lg font-bold text-gray-900">Ödeme</h2>
                <p className="text-2xl font-bold text-[#6b6de3] mt-1">{totalPrice.toFixed(0)} TL</p>
              </div>

              {/* Payment Method - hide when veresiye active */}
              {!isCredit && (
              <div>
                <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider mb-2">Ödeme Yöntemi</p>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { key: 'cash', label: 'Nakit', icon: Banknote },
                    { key: 'card', label: 'Kart', icon: CreditCard },
                    { key: 'split', label: 'Parçalı', icon: ClipboardList },
                  ].map(m => (
                    <button key={m.key} onClick={() => {
                      setPaymentMethod(m.key);
                      if (m.key === 'cash') { setCashAmount(totalPrice.toFixed(0)); setCardAmount(''); }
                      else if (m.key === 'card') { setCardAmount(totalPrice.toFixed(0)); setCashAmount(''); }
                      else { setCashAmount(''); setCardAmount(''); }
                    }}
                      className={`py-3 rounded-xl flex flex-col items-center gap-1.5 transition-all ${
                        paymentMethod === m.key
                          ? 'bg-[#6b6de3] text-white shadow-md'
                          : 'bg-[#F2F2F7] text-gray-700'
                      }`}
                      data-testid={`payment-${m.key}`}
                    >
                      <m.icon size={20} />
                      <span className="text-xs font-medium">{m.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              )}

              {/* Split Payment Inputs - hide when veresiye active */}
              {!isCredit && paymentMethod === 'split' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-[#8E8E93] mb-1 block">Nakit</label>
                    <div className="relative">
                      <Input type="number" value={cashAmount}
                        onChange={(e) => { setCashAmount(e.target.value); setCardAmount((totalPrice - (parseFloat(e.target.value) || 0)).toFixed(0)); }}
                        placeholder="0" className="h-12 bg-[#F2F2F7] border-0 rounded-xl pr-8"
                        data-testid="split-cash-input" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8E8E93]">TL</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-[#8E8E93] mb-1 block">Kart</label>
                    <div className="relative">
                      <Input type="number" value={cardAmount}
                        onChange={(e) => { setCardAmount(e.target.value); setCashAmount((totalPrice - (parseFloat(e.target.value) || 0)).toFixed(0)); }}
                        placeholder="0" className="h-12 bg-[#F2F2F7] border-0 rounded-xl pr-8"
                        data-testid="split-card-input" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8E8E93]">TL</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Fatura Kes Toggle */}
              {parasutActive && (
                <div className="bg-[#F2F2F7] rounded-2xl p-4" data-testid="invoice-section">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Fatura Kes</p>
                      <p className="text-xs text-[#8E8E93]">Paraşüt ile e-Fatura / e-Arşiv</p>
                    </div>
                    <button onClick={() => setCreateInvoice(!createInvoice)} data-testid="invoice-toggle">
                      {createInvoice
                        ? <ToggleRight size={36} className="text-[#6366F1]" />
                        : <ToggleLeft size={36} className="text-[#C7C7CC]" />}
                    </button>
                  </div>
                  {createInvoice && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <label className="text-xs text-[#8E8E93] mb-1 block">TC Kimlik No <span className="text-red-500">*</span></label>
                      <Input type="text" inputMode="numeric" maxLength={11} value={tcKimlikNo}
                        onChange={e => setTcKimlikNo(e.target.value.replace(/\D/g, '').slice(0, 11))}
                        placeholder="11 haneli TC Kimlik No"
                        className="h-12 bg-white border-0 rounded-xl" data-testid="tc-kimlik-input" />
                      {tcKimlikNo && tcKimlikNo.length !== 11 && (
                        <p className="text-xs text-red-500 mt-1">TC Kimlik No 11 haneli olmalıdır</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Veresiye Toggle */}
              <div className="bg-[#F2F2F7] rounded-2xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Veresiye</p>
                    <p className="text-xs text-[#8E8E93]">Kalan tutarı sonra tahsil et</p>
                  </div>
                  <button onClick={() => setIsCredit(!isCredit)} data-testid="credit-toggle">
                    {isCredit
                      ? <ToggleRight size={36} className="text-[#FF9500]" />
                      : <ToggleLeft size={36} className="text-[#C7C7CC]" />}
                  </button>
                </div>

                {isCredit && (
                  <div className="mt-4 space-y-3 pt-3 border-t border-gray-200">
                    <div>
                      <label className="text-xs text-[#8E8E93] mb-1 block">Tahsilat Yöntemi</label>
                      <div className="grid grid-cols-3 gap-2">
                        <button onClick={() => { setCreditMethod('cash'); setCreditCash(''); setCreditCard(''); }}
                          className={`py-2.5 rounded-xl flex items-center justify-center gap-1.5 text-sm font-medium transition-all ${
                            creditMethod === 'cash' ? 'bg-[#6b6de3] text-white' : 'bg-white text-gray-700'}`}
                          data-testid="credit-method-cash">
                          <Banknote size={16} /> Nakit
                        </button>
                        <button onClick={() => { setCreditMethod('card'); setCreditCash(''); setCreditCard(''); }}
                          className={`py-2.5 rounded-xl flex items-center justify-center gap-1.5 text-sm font-medium transition-all ${
                            creditMethod === 'card' ? 'bg-[#6b6de3] text-white' : 'bg-white text-gray-700'}`}
                          data-testid="credit-method-card">
                          <CreditCard size={16} /> Kart
                        </button>
                        <button onClick={() => { setCreditMethod('split'); setCreditCash(''); setCreditCard(''); setCreditCollected(''); }}
                          className={`py-2.5 rounded-xl flex items-center justify-center gap-1.5 text-sm font-medium transition-all ${
                            creditMethod === 'split' ? 'bg-[#6b6de3] text-white' : 'bg-white text-gray-700'}`}
                          data-testid="credit-method-split">
                          <ClipboardList size={16} /> Parçalı
                        </button>
                      </div>
                    </div>
                    {creditMethod !== 'split' ? (
                      <div>
                        <label className="text-xs text-[#8E8E93] mb-1 block">Tahsil Edilen Tutar</label>
                        <div className="relative">
                          <Input type="number" value={creditCollected}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              const max = totalPrice - 1;
                              if (val > max) {
                                setCreditCollected(max > 0 ? String(max) : '0');
                              } else {
                                setCreditCollected(e.target.value);
                              }
                            }}
                            max={totalPrice - 1}
                            min={0}
                            placeholder="0" className="h-12 bg-white border-0 rounded-xl pr-8"
                            data-testid="credit-collected-input" />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8E8E93]">TL</span>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-[#8E8E93] mb-1 block">Nakit</label>
                          <div className="relative">
                            <Input type="number" value={creditCash}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                const cardVal = parseFloat(creditCard) || 0;
                                const max = totalPrice - 1 - cardVal;
                                const clamped = Math.min(val, Math.max(0, max));
                                setCreditCash(val > max ? String(clamped) : e.target.value);
                                setCreditCollected(String(clamped + cardVal));
                              }}
                              placeholder="0" className="h-12 bg-white border-0 rounded-xl pr-8"
                              data-testid="credit-split-cash" />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8E8E93]">TL</span>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-[#8E8E93] mb-1 block">Kart</label>
                          <div className="relative">
                            <Input type="number" value={creditCard}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                const cashVal = parseFloat(creditCash) || 0;
                                const max = totalPrice - 1 - cashVal;
                                const clamped = Math.min(val, Math.max(0, max));
                                setCreditCard(val > max ? String(clamped) : e.target.value);
                                setCreditCollected(String(cashVal + clamped));
                              }}
                              placeholder="0" className="h-12 bg-white border-0 rounded-xl pr-8"
                              data-testid="credit-split-card" />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8E8E93]">TL</span>
                          </div>
                        </div>
                      </div>
                    )}
                    {(parseFloat(creditCollected) || 0) > 0 && (
                      <p className="text-sm text-[#FF9500] font-medium text-center">
                        Kalan: {(totalPrice - (parseFloat(creditCollected) || 0)).toFixed(0)} TL
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowPaymentModal(false)}
                  className="flex-1 h-14 rounded-full text-base" data-testid="payment-cancel-btn">
                  Geri
                </Button>
                <Button onClick={handleSubmit} disabled={loading}
                  className="flex-1 h-14 rounded-full text-base font-semibold bg-[#34C759] hover:bg-[#2db84e]"
                  data-testid="payment-confirm-btn">
                  {loading ? 'Kaydediliyor...' : 'Onayla'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddOperation;
